import { ConversationPost } from '@/types/agent';
import {
  downloadSoulMapDocumentFile,
  SoulMapDocumentDownloadOptions,
  SoulMapDocumentDownloadResult,
} from '@/utils/soulmap-document-download';
import axios from 'axios';
import api from './utils/axios';
import generateCRUDOps from './utils/generateCrud';

const pathPrefix = `/agentos/`;

export interface SoulMapDocumentStatus {
  status:
    | 'not_started'
    | 'in_progress'
    | 'checking'
    | 'unsupported'
    /** The intake is complete and the agent has offered the document (LE-296). */
    | 'offered'
    | 'recomputing_profile'
    | 'queued'
    | 'generating'
    | 'rendering'
    | 'ready'
    | 'failed'
    | 'expired'
    | 'unavailable';

  report_id?: number | null;
  title?: string;
  document_type?: 'soul_map_full';
  delivery_formats?: string[];
  poll_after_seconds?: number;
  completed_at?: string | null;
  /** When the backend recorded the agent's document offer on the practice. */
  offered_at?: string | null;
  confirmed_at?: string | null;
  generated_at?: string | null;
  expires_at?: string | null;
  /**
   * PDF link per edition. The docs-based pipeline (LE-296) returns `full`;
   * the LE-241 pipeline returns `personal` and `shareable`.
   */
  can_retry?: boolean;
  /** Present when generation failed because the birth profile could not recompute. */
  birth_profile_recompute_error?: boolean | string;

  downloads?: {
    full?: string;
    personal?: string;
    shareable?: string;
  };

  formats?: Record<string, Record<string, string>>;

  documents?: Record<
    string,
    Record<
      string,
      {
        url: string;
        download_count: number;
        last_downloaded_at?: string | null;
        /** Optional file details shown on the in-chat document card. */
        size_bytes?: number | null;
        page_count?: number | null;
      }
    >
  >;
  error?: string;
}

const PracticeService = {
  // Use actual CRUD operations
  ...generateCRUDOps(pathPrefix, 'practices'),

  // Custom endpoints with real implementation
  runPractice: async (
    practiceId: number,
    userMessage: string,
    action: string = 'Appreciation',
    docId?: number
  ) => {
    const response = await api.post(
      `${pathPrefix}practices/${practiceId}/run-practice/?action=${action}`,
      {
        user_message: userMessage,
        ...(docId && { doc_id: docId }),
      }
    );
    return response.data;
  },

  getPosts: async (
    practiceId: number,
    options?: { suppressErrorToast?: boolean }
  ) => {
    const response = await api.get(
      `${pathPrefix}practices/${practiceId}/posts/`,
      { suppressErrorToast: options?.suppressErrorToast }
    );
    return response.data;
  },

  createPosts: async (practiceId: number, postData: unknown) => {
    const response = await api.post(
      `${pathPrefix}practices/${practiceId}/posts/`,
      postData
    );
    return response.data;
  },

  updatePosts: async (practiceId: number, postData: unknown) => {
    const response = await api.patch(
      `${pathPrefix}practices/${practiceId}/posts/`,
      postData
    );
    return response.data;
  },

  addToConversation: async (practiceId: number, posts: ConversationPost) => {
    const response = await api.post(
      `${pathPrefix}practices/${practiceId}/add_conversation/`,
      { posts }
    );
    return response.data;
  },

  getSoulMapDocuments: async (
    practiceId: number
  ): Promise<SoulMapDocumentStatus | null> => {
    try {
      const response = await api.get<SoulMapDocumentStatus>(
        `${pathPrefix}practices/${practiceId}/soul-map-documents/`,
        { suppressErrorToast: true }
      );
      return response.data;
    } catch (error) {
      // The frontend may be deployed before the document backend. Treat an
      // unknown endpoint as an unavailable optional capability, not a chat error.
      if (
        axios.isAxiosError(error) &&
        (error.response?.status === 404 || error.response?.status === 405)
      ) {
        return { status: 'unavailable' };
      }
      throw error;
    }
  },

  downloadSoulMapDocument: async (
    downloadUrl: string,
    fallbackFilename: string,
    options?: SoulMapDocumentDownloadOptions
  ): Promise<SoulMapDocumentDownloadResult> => {
    return downloadSoulMapDocumentFile(downloadUrl, fallbackFilename, options);
  },

  /**
   * 202 `{status: 'queued'}`; 409 with the current `status` and a `reason`
   * when the report is not retryable; 404 when there is no report. Callers
   * show their own errors, since a 409 is often a retry that already ran.
   */
  retrySoulMapDocument: async (
    practiceId: number
  ): Promise<SoulMapDocumentStatus> => {
    const response = await api.post<SoulMapDocumentStatus>(
      `${pathPrefix}practices/${practiceId}/soul-map-documents/retry/`,
      {},
      { suppressErrorToast: true }
    );
    return response.data;
  },

  /**
   * Deterministic confirmation from the in-chat card (LE-296, F1 option B).
   * 202 with the queued report; 400 `{status: 'refused', reason}` when a
   * permission check fails.
   */
  confirmSoulMapDocument: async (
    practiceId: number
  ): Promise<SoulMapDocumentStatus> => {
    const response = await api.post<SoulMapDocumentStatus>(
      `${pathPrefix}practices/${practiceId}/soul-map-documents/confirm/`,
      {},
      { suppressErrorToast: true }
    );
    return response.data;
  },

  /**
   * Every practice of a user, following pagination. Used by background checks,
   * so errors do not toast.
   */
  getAllUserPractices: async <T = Record<string, unknown>>(
    userId: number | string,
    pageSize = 100
  ): Promise<T[]> => {
    const results: T[] = [];
    // Hard stop so a misbehaving `next` cannot loop forever.
    for (let page = 1; page <= 50; page += 1) {
      const { data } = await api.get(`${pathPrefix}practices/`, {
        params: { user_id: userId, page, page_size: pageSize },
        suppressErrorToast: true,
      });
      if (Array.isArray(data)) return data as T[];
      results.push(...((data?.results ?? []) as T[]));
      if (!data?.next) break;
    }
    return results;
  },

  // getPractices: async (page = 1, pageSize = 100) => {
  //   try {
  //     const response = await api.get(`${pathPrefix}practices/`, {
  //       params: {
  //         page,
  //         page_size: pageSize,
  //       },
  //     });
  //     return response.data;
  //   } catch (error) {
  //     throw error;
  //   }
  // },
};

export default PracticeService;
